/**
 * 内置 Skill：UI 设计专家
 */

import { UI_DESIGN_EXPERT_SYSTEM } from '@/utils/uiDesignExpertSystemPrompt'
import {
  UI_DESIGN_DEFAULT_PRIMARY,
  UI_DESIGN_DEFAULT_SECONDARY,
  UI_DESIGN_PLATFORMS,
  resolveUiDesignStyleText,
  uiDesignStyleLabel,
  normalizeUiDesignScreenCount
} from '@/utils/uiDesignNode'
import { formatReferenceStyleGuideBlock } from '@/utils/uiDesignUpstreamStyleAnalyze'

export const UI_SKILL_DESIGN_EXPERT_ID = 'ui-skill-design-expert'

export { UI_DESIGN_EXPERT_SYSTEM } from '@/utils/uiDesignExpertSystemPrompt'

export function isUiDesignExpertSkill(skill) {
  if (!skill || typeof skill !== 'object') return false
  const id = String(skill.id || '').trim()
  if (id === UI_SKILL_DESIGN_EXPERT_ID) return true
  const title = String(skill.title || '').trim()
  return title.includes('UI设计专家')
}

export function uiDesignExpertSystemForSkill(skill) {
  const custom = skill && skill.systemPrompt != null ? String(skill.systemPrompt).trim() : ''
  return custom || UI_DESIGN_EXPERT_SYSTEM
}

const DISPLAY_MODE_LABELS = {
  frameless: '无边框纯界面（默认，推荐）',
  iphone: 'iPhone 15 Pro 银色模型内展示',
  desktop: 'MacBook / 桌面显示器模型内展示（16:9 宽屏 UI）'
}

/**
 * @param {object} ctx
 */
export function buildUiDesignExpertUserMessage(ctx) {
  const c = ctx && typeof ctx === 'object' ? ctx : {}
  const name = String(c.projectName || '').trim()
  const styleText = resolveUiDesignStyleText(c)
  const styleLabel = uiDesignStyleLabel(c)
  const primary = String(c.themeColorPrimary || UI_DESIGN_DEFAULT_PRIMARY).trim()
  const secondary = String(c.themeColorSecondary || UI_DESIGN_DEFAULT_SECONDARY).trim()
  const plat = String(c.platform || 'app').trim()
  const platLabel = (UI_DESIGN_PLATFORMS.find(p => p.id === plat) || {}).label || plat
  const displayId = String(c.displayMode || 'frameless').trim()
  const displayLabel = DISPLAY_MODE_LABELS[displayId] || displayId
  const screenCount = normalizeUiDesignScreenCount(c.screenCount)
  const mainCount = Math.min(3, screenCount)
  const refGuide = String(
    c.referenceStyleGuide || c.upstreamImageInfo || ''
  ).trim()
  const refBlock = formatReferenceStyleGuideBlock(refGuide, name)

  const lines = [
    `请严格按「UI设计专家（风格模板版）」技能输出：先 ### 全局设计规范声明（标题注明风格模板名），再输出恰好 ${screenCount} 张 UI 界面生图提示词。`,
    `前 ${mainCount} 张主图标注 [1024x1024]，第 ${mainCount + 1} 张起为详情图并标注 [1024x1536]（若仅 ${screenCount} 张则全部按主图尺寸亦可，但须凑满 ${screenCount} 张）。`,
    '每张末尾须含「严格遵循全局设计规范声明中的所有参数」；全局规范须含具体 HEX、圆角 px、字体名称。',
    '',
    '【总任务】按所选内置风格模板（默认极简扁平风）统一视觉；为「待设计产品」规划功能界面；若有参考 AI 风格则视觉融合参考图、禁止照搬参考图业务页面。'
  ]

  if (refBlock) {
    lines.push(refBlock)
    lines.push('')
  }

  lines.push('【待设计产品 · 功能与页面以此为准】')
  lines.push(`产品名称：${name || '（请根据需求推断）'}`)
  lines.push(`目标平台：${platLabel}`)
  lines.push(`界面数量：${screenCount} 张（将拆分为 ${screenCount} 个下游图片节点）`)
  lines.push(`展示方式：${displayLabel}`)
  lines.push(`主题色主色：${primary}`)
  lines.push(`主题色辅色：${secondary}`)
  if (styleLabel || styleText) {
    lines.push(
      `风格模板（${styleLabel || '极简扁平风'}，须写入全局规范声明标题；可与参考风格合并）：\n${styleText || '极简扁平风'}`
    )
  } else {
    lines.push('风格模板：未指定，请默认使用「极简扁平风」完整参数。')
  }
  const brief = String(c.brief || '').trim()
  if (brief) lines.push(`功能与页面补充：\n${brief}`)
  const extra = String(c.extraPrompt || '').trim()
  if (extra) lines.push(`其它要求：${extra}`)
  const up = String(c.upstreamText || '').trim()
  if (up) lines.push(`【其它上游文案（非风格参考）】\n${up}`)

  if (!name && !brief && !up && !refGuide) {
    lines.push('（请填写待设计产品名称，或提供参考风格与需求说明）')
  } else if (refGuide && name) {
    lines.push(
      `（请为「${name}」设计完整用户路径界面；全局规范须融合参考 AI 风格与上述主题色，页面内容不得沿用参考图业务）`
    )
  }
  return lines.join('\n\n')
}

export function getUiDesignExpertBuiltinSkill() {
  return {
    id: UI_SKILL_DESIGN_EXPERT_ID,
    title: 'UI设计专家（风格模板版）',
    desc: '20 种内置 UI 风格模板 + 产品名自动分析，8+ 屏提示词',
    prompt:
      '选择内置 UI 风格（默认极简扁平风）与产品名称，输出全局规范与 8+ 张高保真 UI 生图提示词。',
    systemPrompt: UI_DESIGN_EXPERT_SYSTEM,
    batchMode: true
  }
}
